import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { potreeLayerApi } from '../app/potree.js';

const S3_URI_HELP =
  's3://sites-orthophotos/projects/my-site/2024-06/pointclouds/cloud_01/metadata.json';

const ProjectPotreeLayersHandler = ({ missions, setMissions, setSnackbar }) => {
  const isCornisAdmin = useSelector((state) => state.auth.role === 'admin');
  const [creating, setCreating] = useState(false);

  const [form, setForm] = useState({
    name: '',
    missionId: missions[0]?.id ?? '',
    metadataUrl: '',
  });

  const potreeLayers = (missions ?? []).flatMap((mission) =>
    (mission.potreeLayers ?? []).map((pl) => ({ ...pl, missionName: mission.name, missionId: mission.id }))
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    const { name, missionId, metadataUrl } = form;
    if (!name?.trim() || !missionId || !metadataUrl?.trim()) {
      setSnackbar({ open: true, message: 'All fields are required', severity: 'warning' });
      return;
    }
    setCreating(true);
    try {
      const created = await potreeLayerApi.create({
        name: name.trim(),
        missionId,
        metadataUrl: metadataUrl.trim(),
        projectUrl: '',
      });
      if (!created?.id) {
        setSnackbar({ open: true, message: 'Failed to create layer', severity: 'error' });
        return;
      }
      setMissions((prev) =>
        prev.map((mission) =>
          mission.id === missionId
            ? {
                ...mission,
                potreeLayers: [...(mission.potreeLayers ?? []), created],
              }
            : mission
        )
      );
      setForm({
        name: '',
        missionId: missions[0]?.id ?? '',
        metadataUrl: '',
      });
      setSnackbar({ open: true, message: '3D layer created', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.message || 'Failed to create layer',
        severity: 'error',
      });
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!isCornisAdmin) return;
    const ok = await potreeLayerApi.delete(id);
    if (!ok) {
      setSnackbar({ open: true, message: 'Failed to delete layer', severity: 'error' });
      return;
    }
    setSnackbar({ open: true, message: '3D layer deleted', severity: 'success' });
    setMissions((prev) =>
      prev.map((mission) => ({
        ...mission,
        potreeLayers: (mission.potreeLayers ?? []).filter((pl) => pl.id !== id),
      }))
    );
  };

  return (
    <Box sx={{ maxWidth: 1200, margin: 'auto', color: 'white' }}>
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom color="white">
          3D layers (Potree)
        </Typography>

        <Typography variant="subtitle1" gutterBottom color="white">
          Existing layers
        </Typography>
        {potreeLayers.length === 0 ? (
          <Typography variant="body2" color="gray">No 3D layer on this project.</Typography>
        ) : (
          <TableContainer component={Paper} sx={{ backgroundColor: '#2a2a2a', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: 'white' }}>Mission</TableCell>
                  <TableCell sx={{ color: 'white' }}>Name</TableCell>
                  <TableCell sx={{ color: 'white' }}>metadataUrl</TableCell>
                  <TableCell sx={{ color: 'white' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {potreeLayers.map((pl) => (
                  <TableRow key={pl.id}>
                    <TableCell sx={{ color: 'white' }}>{pl.missionName}</TableCell>
                    <TableCell sx={{ color: 'white' }}>{pl.name}</TableCell>
                    <TableCell sx={{ color: 'white', fontSize: '0.75rem' }}>{pl.metadataUrl}</TableCell>
                    <TableCell align="right">
                      {isCornisAdmin && (
                        <IconButton size="small" color="error" onClick={() => handleDelete(pl.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom color="white">
          New 3D layer
        </Typography>
        <Typography variant="body2" color="gray" sx={{ mb: 1 }}>
          S3 URIs only. <code>metadataUrl</code> = Potree octree <code>metadata.json</code>
          (with <code>octree.bin</code> / <code>hierarchy.bin</code> beside it).
          Example: <code>{S3_URI_HELP}</code>
        </Typography>
        <Box component="form" onSubmit={handleCreate}>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          />
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel id="potree-mission-label">Mission</InputLabel>
            <Select
              labelId="potree-mission-label"
              label="Mission"
              value={form.missionId}
              onChange={(e) => setForm((f) => ({ ...f, missionId: e.target.value }))}
            >
              {missions.map((m) => (
                <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            fullWidth
            margin="normal"
            size="small"
            label="metadataUrl"
            placeholder="s3://sites-orthophotos/projects/my-site/2024-06/pointclouds/cloud_01/metadata.json"
            value={form.metadataUrl}
            onChange={(e) => setForm((f) => ({ ...f, metadataUrl: e.target.value }))}
          />
          <Button variant="contained" type="submit" sx={{ mt: 2 }} disabled={creating}>
            {creating ? 'Creating…' : 'Create'}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectPotreeLayersHandler;
